// A date-range filter: preset rows plus a custom from/to pair, in a popover.
//
// Deliberately the SAME shape as `MultiSelect` beside it (32px trigger showing
// the current selection, popover of options, Clear at the foot, close on
// outside-press or Escape with focus returned), because the two sit in the same
// filter strips across the admin console and a control that behaves differently
// from its neighbour is a control people mistrust.
//
// WHY BOTH presets and a custom pair: "last 30 days" is the overwhelmingly
// common ask and should be one click, but the question that actually needs a
// filter is usually "what happened during that incident" or "what did the week
// of the launch look like", which no fixed preset can express.
//
// The component is CONTROLLED and holds no filter state beyond the popover's
// open flag: the page owns the range, so the headline numbers, the table, and
// the URL are all folded from one source.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import {
  DATE_RANGE_PRESETS,
  type DateRange,
  EMPTY_DATE_RANGE,
  dateRangeSummary,
  isDateRangeActive,
  matchingPresetDays,
  normalizeDateRange,
  rangeForDays,
} from "./date-range";
import "./date-range-filter.css";
import { shouldAlignEnd } from "./popover-align";

export interface DateRangeFilterProps {
  /** What the range is OVER, for the trigger's accessible name, e.g. "Raised".
   *  Every page names its own column: the same control filters projects by when
   *  they were created and turns by when they ended, and a generic "Date" would
   *  leave the reader guessing which. */
  label: string;
  value: DateRange;
  onChange: (next: DateRange) => void;
  /** Latest day the pickers accept. Defaults to today: every surface this
   *  filters is a record of something that already happened, so a future bound
   *  can only ever return the same rows as today while suggesting otherwise. */
  max?: string;
}

export function DateRangeFilter({ label, value, onChange, max }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [alignEnd, setAlignEnd] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const fromId = useId();
  const toId = useId();

  // Which edge the panel hangs from, decided from where the trigger actually
  // sits. A filter pinned to the RIGHT of a page header has no room to open
  // rightward, and an absolutely-positioned panel is clipped by nothing, so it
  // would run off the window with the date pickers cut in half. Measured in a
  // layout effect (before paint, so the panel never flashes in the wrong
  // place); `panelWidth` is the panel's own width, which does not depend on the
  // alignment being measured, so this settles in one pass.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const rect = trigger.getBoundingClientRect();
    setAlignEnd(
      shouldAlignEnd({
        triggerLeft: rect.left,
        triggerRight: rect.right,
        panelWidth: panel.offsetWidth,
        viewportWidth: window.innerWidth,
      }),
    );
  }, [open]);

  // Close on an outside press or Escape. Both listeners are torn down with the
  // popover rather than living for the page's lifetime.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Escape must land focus back on the control that opened the popover,
      // otherwise a keyboard user is dropped at the top of the document.
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // A bound is edited one half at a time, and an empty input is that half
  // CLEARED rather than a malformed day. `normalizeDateRange` then drops
  // anything that is not a real date and reorders a backwards pair, so typing
  // the end before the start is not an error state the operator has to escape.
  const setBound = useCallback(
    (key: "from" | "to") => (raw: string) => {
      onChange(normalizeDateRange({ ...value, [key]: raw === "" ? null : raw }));
    },
    [onChange, value],
  );

  const active = isDateRangeActive(value);
  const activePreset = matchingPresetDays(value);
  const today = max ?? new Date().toISOString().slice(0, 10);

  return (
    <div className="dr-root" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={active ? "dr-trigger dr-trigger--active" : "dr-trigger"}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dr-trigger__text">{dateRangeSummary(value)}</span>
        <span aria-hidden="true" className="dr-trigger__caret">
          ▾
        </span>
      </button>

      {open ? (
        <div
          className={alignEnd ? "dr-panel dr-panel--end" : "dr-panel"}
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={label}
        >
          <div className="dr-presets">
            {DATE_RANGE_PRESETS.map((preset) => {
              const isOn = preset.days === null ? !active : preset.days === activePreset;
              return (
                <button
                  key={preset.label}
                  type="button"
                  className={isOn ? "dr-preset dr-preset--on" : "dr-preset"}
                  aria-pressed={isOn}
                  onClick={() =>
                    onChange(preset.days === null ? EMPTY_DATE_RANGE : rangeForDays(preset.days))
                  }
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          <div className="dr-custom">
            <div className="dr-field">
              <label className="dr-field__label" htmlFor={fromId}>
                From
              </label>
              <input
                id={fromId}
                className="dr-field__input"
                type="date"
                max={today}
                value={value.from ?? ""}
                onChange={(e) => setBound("from")(e.currentTarget.value)}
              />
            </div>
            <div className="dr-field">
              <label className="dr-field__label" htmlFor={toId}>
                To
              </label>
              <input
                id={toId}
                className="dr-field__input"
                type="date"
                max={today}
                value={value.to ?? ""}
                onChange={(e) => setBound("to")(e.currentTarget.value)}
              />
            </div>
          </div>

          <button
            type="button"
            className="dr-clear"
            disabled={!active}
            onClick={() => onChange(EMPTY_DATE_RANGE)}
          >
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}
